import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Calendar,
  MapPin,
  Phone,
  FileText,
  CheckCircle2,
  Clock,
  AlertCircle,
  Navigation,
  Building2,
  MessageSquare
} from 'lucide-react';
import { format, isToday, isTomorrow, isPast, differenceInDays } from 'date-fns';
import { de } from 'date-fns/locale';
import { cn } from '@/lib/utils';

/**
 * Görev tipi ikonları
 */
const taskTypeIcons = {
  'Erstbesuch': MapPin,
  'Besuchen': Navigation,
  'Anrufen': Phone,
  'Angebot': FileText,
  'Nachfassen': Clock,
  'Lieferung': Building2,
  'Reklamation': AlertCircle,
  'Bedarfsabfrage': MessageSquare,
};

/**
 * Görev tipi renkleri (yumuşak tonlu ikon rozeti — ferah/sade)
 */
const taskTypeColors = {
  'Erstbesuch': 'bg-brand-50 text-brand-700',
  'Besuchen': 'bg-blue-50 text-blue-700',
  'Anrufen': 'bg-emerald-50 text-emerald-700',
  'Angebot': 'bg-amber-50 text-amber-700',
  'Nachfassen': 'bg-purple-50 text-purple-700',
  'Lieferung': 'bg-cyan-50 text-cyan-700',
  'Reklamation': 'bg-red-50 text-red-700',
  'Bedarfsabfrage': 'bg-pink-50 text-pink-700',
};

/**
 * Task Card Component
 *
 * @param {Object} task - Görev objesi
 * @param {boolean} showActions - Aksiyon butonlarını göster
 * @param {function} onComplete - Tamamla callback
 * @param {function} onClick - Tıklama callback
 * @param {boolean} compact - Kompakt görünüm
 */
export default function TaskCard({
  task,
  showActions = true,
  onComplete,
  onClick,
  compact = false,
}) {
  const dueDate = task.due_date ? new Date(task.due_date) : null;
  const isOverdue = dueDate && isPast(dueDate) && task.status === 'Offen';
  const isDueToday = dueDate && isToday(dueDate);
  const isDueTomorrow = dueDate && isTomorrow(dueDate);

  const TaskIcon = taskTypeIcons[task.task_type] || Calendar;

  const getOpeningStatus = () => {
    const hoursData = task.lead_opening_hours;
    if (!hoursData || !hoursData.periods || hoursData.periods.length === 0) return null;

    const now = new Date();
    const currentDay = now.getDay();
    const currentHour = now.getHours();
    const currentMin = now.getMinutes();
    const currentTime = currentHour * 100 + currentMin; 
    
    const todaysPeriods = hoursData.periods.filter(p => p.open && p.open.day === currentDay);
    
    if (todaysPeriods.length === 0) {
      return { isOpen: false, text: 'Geschlossen' };
    }
    
    if (todaysPeriods.length === 1 && todaysPeriods[0].open.time === '0000' && !todaysPeriods[0].close) {
      return { isOpen: true, text: '24h geöffnet' };
    }

    for (const period of todaysPeriods) {
      if (!period.open || !period.open.time) continue;
      const openTime = parseInt(period.open.time, 10);
      const closeTime = period.close ? parseInt(period.close.time, 10) : 2359;
      
      let isCurrentlyOpen = false;
      if (closeTime < openTime) {
        if (currentTime >= openTime || currentTime <= closeTime) isCurrentlyOpen = true;
      } else {
        if (currentTime >= openTime && currentTime < closeTime) isCurrentlyOpen = true;
      }
      
      if (isCurrentlyOpen) {
        const closeStr = period.close ? `${period.close.time.slice(0,2)}:${period.close.time.slice(2)}` : '';
        return { isOpen: true, text: closeStr ? `Geöffnet bis ${closeStr}` : 'Geöffnet' };
      }
    }
    
    return { isOpen: false, text: 'Geschlossen' };
  };

  const openingData = getOpeningStatus();

  const getDueDateLabel = () => {
    if (!dueDate) return '';
    if (isOverdue) {
      const daysOverdue = Math.abs(differenceInDays(dueDate, new Date()));
      return `${daysOverdue} Tag${daysOverdue > 1 ? 'e' : ''} überfällig`;
    }
    if (isDueToday) return 'Heute';
    if (isDueTomorrow) return 'Morgen';
    return format(dueDate, 'dd. MMM', { locale: de });
  };

  const getDueDateColor = () => {
    if (task.status === 'Abgeschlossen') return 'text-slate-400';
    if (isOverdue) return 'text-red-600 font-semibold';
    if (isDueToday) return 'text-amber-600 font-medium';
    if (isDueTomorrow) return 'text-blue-600';
    return 'text-slate-600';
  };

  const openNavigation = (e) => {
    e.stopPropagation();
    if (task.lead_address) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(task.lead_address)}`,
        '_blank'
      );
    }
  };

  const handleComplete = (e) => {
    e.stopPropagation();
    onComplete?.(task);
  };

  return (
    <Card
      className={cn(
        'transition-all hover:shadow-md',
        task.status === 'Abgeschlossen' && 'opacity-60 bg-slate-50',
        isOverdue && 'border-l-4 border-l-red-500',
        isDueToday && task.status === 'Offen' && 'border-l-4 border-l-amber-500',
        onClick && 'cursor-pointer'
      )}
      onClick={() => onClick?.(task)}
    >
      <div className={cn('p-4', compact && 'p-3')}>
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className={cn(
              'w-9 h-9 rounded-xl flex items-center justify-center shrink-0',
              task.status === 'Abgeschlossen'
                ? 'bg-slate-100 text-slate-400'
                : (taskTypeColors[task.task_type] || 'bg-slate-100 text-slate-600')
            )}>
              <TaskIcon className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className={cn(
                'font-semibold truncate',
                compact ? 'text-sm' : 'text-base',
                task.status === 'Abgeschlossen' && 'line-through text-slate-500'
              )}>
                {task.lead_name}
              </h3>
            </div>
          </div>

          <Badge
            variant="outline"
            className={cn(
              'flex-shrink-0',
              taskTypeColors[task.task_type] || 'bg-slate-100 text-slate-600'
            )}
          >
            {task.task_type}
          </Badge>
        </div>

        {/* Address */}
        {task.lead_address && !compact && (
          <button
            onClick={openNavigation}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-blue-600 mb-2 w-full text-left"
          >
            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{task.lead_address}</span>
          </button>
        )}

        {/* Opening Hours Info */}
        {openingData && !compact && (
          <div className="flex items-center gap-2 mb-2">
            <span className={cn(
              "inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-medium border",
              openingData.isOpen 
                ? "bg-emerald-50 text-emerald-700 border-emerald-200" 
                : "bg-rose-50 text-rose-700 border-rose-200"
            )}>
              <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
              {openingData.text}
            </span>
          </div>
        )}

        {/* Info Block */}
        {task.info_block && !compact && (
          <p className="text-sm text-slate-600 mb-2 line-clamp-2">
            {task.info_block}
          </p>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between mt-3">
          <div className={cn('flex items-center gap-1.5 text-sm min-w-0', getDueDateColor())}>
            <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{getDueDateLabel()}</span>
          </div>

          {showActions && task.status === 'Offen' && (
            <div className="flex gap-2 flex-shrink-0">
              {task.lead_address && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openNavigation}
                  className="h-9 w-9 min-w-[44px] min-h-[44px] px-2"
                >
                  <Navigation className="w-4 h-4" />
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleComplete}
                className="h-9 whitespace-nowrap min-w-[44px] min-h-[44px] bg-emerald-600 hover:bg-emerald-700"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Erledigt
              </Button>
            </div>
          )}

          {task.status === 'Abgeschlossen' && (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Erledigt
            </Badge>
          )}
        </div>
      </div>
    </Card>
  );
}

/**
 * Görev listesi için gruplandırma fonksiyonu
 * @param {Array} tasks - Görev listesi
 * @returns {Object} Gruplandırılmış görevler
 */
export function groupTasksByDate(tasks) {
  const groups = {
    overdue: [],
    today: [],
    tomorrow: [],
    thisWeek: [],
    later: [],
    completed: [],
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  tasks.forEach(task => {
    if (task.status === 'Abgeschlossen') {
      groups.completed.push(task);
      return;
    }

    const dueDate = task.due_date ? new Date(task.due_date) : null;
    if (!dueDate) {
      groups.later.push(task);
      return;
    }

    dueDate.setHours(0, 0, 0, 0);
    const daysDiff = differenceInDays(dueDate, today);

    if (daysDiff < 0) {
      groups.overdue.push(task);
    } else if (daysDiff === 0) {
      groups.today.push(task);
    } else if (daysDiff === 1) {
      groups.tomorrow.push(task);
    } else if (daysDiff <= 7) {
      groups.thisWeek.push(task);
    } else {
      groups.later.push(task);
    }
  });

  return groups;
}
